import styled from "@emotion/styled";
import { LocalDate, LocalTime } from "@js-joda/core";
import { motion } from "framer-motion";
import React from "react";
import Alert from "./components/Alert";
import { AppButton } from "./components/Generic/AppButton";
import { BoxShimmer } from "./components/Shimmer";
import useScrollInfo from "./hooks/useScrollInfo";
import { STRINGS } from "./strings";
import { globalize, translate } from "./translate";
import { toTitleCase } from "./utils/stringUtils";

type Props =
  | {
      status: "loaded";
      slots: DaySlot[];
      message: string | null;
      selectedDate: LocalDate | null;
      selectedTime: LocalTime | null;
      shouldShowError: boolean;
      selectDateLabelRef?: React.RefObject<HTMLDivElement>;
      selectTimeLabelRef?: React.RefObject<HTMLDivElement>;
      onSelectDate: (date: LocalDate) => void;
      onSelectTime: (time: LocalTime) => void;
    }
  | {
      status: "loading" | "error";
    };

type TimeSlotRange = { from: LocalTime; to: LocalTime };
export type DaySlot = { day: LocalDate; timeSlots: TimeSlotRange[] };

const SelectTimeSlot: React.FC<Props> = (props) => {
  switch (props.status) {
    case "error":
      return <Alert type="danger">{translate(STRINGS.ERROR_FETCHING_SLOTS)}</Alert>;

    case "loading":
      return <SelectTimeSlotLoading />;

    case "loaded":
      return <SelectTimeSlotLoaded {...props} />;
  }
};

const SelectTimeSlotLoaded: React.FC<Props & { status: "loaded" }> = ({
  slots,
  message,
  selectedDate,
  selectedTime,
  shouldShowError,
  selectDateLabelRef,
  selectTimeLabelRef,
  onSelectDate,
  onSelectTime,
}) => {
  const maxDefaultVisibleTimeSlots = 18;
  const [showAll, setShowAll] = React.useState(false);

  const timeSlots = React.useMemo(
    () =>
      generateTimeSlots(
        slots.find(({ day }) => selectedDate?.equals(day) ?? false)?.timeSlots ?? []
      ),
    [selectedDate, slots]
  );
  const cardContainerRef = React.useRef<HTMLDivElement>(null);
  const cardRefs = React.useRef<HTMLDivElement[]>([]);

  React.useEffect(() => {
    cardRefs.current = cardRefs.current.slice(0, slots.length);
  }, [slots]);

  const { isEndOfScroll, isTouched: hasScrolled } = useScrollInfo({
    ref: cardContainerRef,
    direction: "horizontal",
    offset: 20,
  });

  const handleClickDateCard = (date: LocalDate, i: number) => {
    onSelectDate(date);

    const card = cardRefs.current[i];

    card.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "center",
    });
  };

  return (
    <Container>
      {message !== null && (
        <AlertWrapper>
          <Alert type="info">{message}</Alert>
        </AlertWrapper>
      )}
      <SelectDateWrapper>
        <Title ref={selectDateLabelRef} isError={selectedDate === null && shouldShowError}>
          {translate(STRINGS.SELECT_DATE)}
        </Title>
        <ArrowRight
          hasScrolled={hasScrolled}
          animate={{ x: [0, 5, 0, 5, 0] }}
          transition={{ repeat: Infinity, duration: 2, repeatDelay: 1 }}
        >
          →
        </ArrowRight>
      </SelectDateWrapper>
      <CardWrapper isEndOfScroll={isEndOfScroll}>
        <CardContainer ref={cardContainerRef}>
          {slots.map(({ day, timeSlots }, index) => (
            <Card
              ref={(element) => element !== null && (cardRefs.current[index] = element)}
              key={index}
              selected={selectedDate?.equals(day) ?? false}
              onClick={() => handleClickDateCard(day, index)}
            >
              <strong>{formatDate(day)}</strong>
              <div>{formatDay(day)}</div>
              <CardSmallText>
                {formatTime(timeSlots[0].from)} - {formatTime(timeSlots[timeSlots.length - 1].to)}
              </CardSmallText>
            </Card>
          ))}
          {slots.length === 0 && <span>{translate(STRINGS.NO_AVAILABLE_SLOTS)}</span>}
        </CardContainer>
      </CardWrapper>

      {selectedDate !== null && (
        <>
          <Title ref={selectTimeLabelRef} isError={selectedTime === null && shouldShowError}>
            Select start time
          </Title>
          <GridWrapper>
            <Grid>
              {timeSlots
                .slice(0, showAll ? timeSlots.length : maxDefaultVisibleTimeSlots)
                .map((time, index) => (
                  <TimeButton
                    key={index}
                    selected={selectedTime?.equals(time) ?? false}
                    onClick={() => onSelectTime(time)}
                  >
                    {formatTime(time)}
                  </TimeButton>
                ))}
            </Grid>
            {timeSlots.length > maxDefaultVisibleTimeSlots && (
              <ShowMoreButton variant="link" onClick={() => setShowAll((prev) => !prev)}>
                {showAll
                  ? toTitleCase(translate(STRINGS.SHOW_LESS))
                  : toTitleCase(translate(STRINGS.SHOW_MORE))}
              </ShowMoreButton>
            )}
          </GridWrapper>
          {timeSlots.length === 0 && <span>{translate(STRINGS.NO_AVAILABLE_SLOTS)}</span>}
        </>
      )}
    </Container>
  );
};

const ShowMoreButton = styled(AppButton)`
  margin-top: 8px;
`;

const SelectTimeSlotLoading: React.FC = () => {
  return (
    <Container>
      <Title isError={false}>Select date</Title>
      <CardContainer>
        {Array.from({ length: 7 }).map((_, index) => (
          <CardSkeleton key={index} width={78} height={78} />
        ))}
      </CardContainer>
    </Container>
  );
};

const formatDate = (date: LocalDate) =>
  `${toTitleCase(date.month().name().slice(0, 3))} ${date.dayOfMonth()}`;

const formatDay = (date: LocalDate) => globalize().formatDayOfWeek(date.dayOfWeek(), false);

const formatTime = (time: LocalTime) => {
  return globalize().formatLocalTime(time, { time: "short" });
};

const generateTimeSlots = (timeSlotRanges: TimeSlotRange[]) => {
  const slots: LocalTime[] = [];
  const midnight = LocalTime.of(0, 0);
  timeSlotRanges.forEach(({ from, to }) => {
    let current = from;
    while (current.isBefore(to) || current.equals(to)) {
      slots.push(current);
      if (current.plusMinutes(15).equals(midnight)) break;
      current = current.plusMinutes(15);
    }
  });
  return slots;
};

const Container = styled.div``;

const SelectDateWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.span<{ isError: boolean }>`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: ${({ isError }) => (isError ? "var(--tw-red-500)" : "var(--tw-slate-800)")};
`;

const CardContainer = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
  align-items: stretch;

  overflow-x: auto;
  overflow-y: hidden;
  white-space: nowrap;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const ArrowRight = styled(motion.div)<{ hasScrolled: boolean }>`
  font-size: 20px;
  color: var(--tw-slate-400);
  pointer-events: none;
  margin-bottom: 8px;
  opacity: ${({ hasScrolled }) => (hasScrolled ? 0 : 1)};
  transition: opacity 200ms ease-in-out;
`;

const CardWrapper = styled.div<{ isEndOfScroll: boolean }>`
  position: relative;

  &::after {
    content: "";
    position: absolute;
    top: 0;
    right: 0;
    width: 6ch;
    height: 100%;
    background: linear-gradient(to left, white, transparent);
    pointer-events: none;
    opacity: ${({ isEndOfScroll }) => (isEndOfScroll ? 0 : 1)};
    transition: opacity 200ms ease-in-out;
  }
`;

const Card = styled.div<{ selected: boolean }>`
  padding: 16px;
  border: 1px solid var(--tw-slate-200);
  border-radius: 8px;
  cursor: pointer;
  background-color: ${({ selected }) => (selected ? "var(--tw-blue-500)" : "transparent")};
  border-color: ${({ selected }) => (selected ? "var(--tw-blue-500)" : "var(--tw-slate-200)")};
  color: ${({ selected }) => (selected ? "white" : "var(--tw-gray-800)")};
  flex-shrink: 0;

  div {
    color: ${({ selected }) => (selected ? "white" : "var(--tw-slate-500)")};
  }
`;

const CardSmallText = styled.div`
  font-size: 14px;
`;

const GridWrapper = styled.div`
  margin-bottom: 24px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(90px, 1fr));
  gap: 12px;
`;

const TimeButton = styled.button<{ selected: boolean }>`
  padding: 8px 12px;
  background-color: ${({ selected }) => (selected ? "var(--tw-blue-500)" : "transparent")};
  border-color: ${({ selected }) => (selected ? "var(--tw-blue-500)" : "var(--tw-slate-200)")};
  color: ${({ selected }) => (selected ? "white" : "var(--tw-gray-800)")};
  border: 1px solid var(--tw-slate-200);
  border-radius: 8px;
  font-size: 15px;
  cursor: pointer;
`;

const CardSkeleton = styled(BoxShimmer)`
  flex-shrink: 0;
`;

const AlertWrapper = styled.div`
  margin-bottom: 16px;
`;

export default SelectTimeSlot;
